import { useEffect, useRef, useState } from "react";
import { AddOnTextInput, Button, Icon, Modal, Typography } from "@/components/atoms";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setFixedPrice } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectFixedPrice } from "@/redux/slices/clientBooking/selectors";
import { clsx, formatCurrency } from "@/utils";

/** AUD, 2dp, no cents conversion — matches the rest of the Price Summary (QuotePriceSummary.fmt). */
const fmt = (value: number) => formatCurrency(value, 2, "AUD", { skipDecimalConversion: true });

/** Minimum charge the backend accepts (BookingManager\Validator\FixedPriceValidator). Mirror it here so
 *  the operator can't submit an amount the create call would reject. */
const MIN_OVERRIDE = 5;

interface JourneyCostOverrideProps {
  /**
   * The live quote's Journey Cost (already reflects an active override, since the re-quote prices the
   * base off `fixed_price`). Undefined when the draft isn't quotable yet — e.g. a fixed-rate transfer
   * type awaiting its first price, which starts this control in "enter a price" mode.
   */
  calculatedValue?: number;
}

/**
 * Operator-only Journey Cost override, rendered in the Price Summary's Journey Cost value cell (see
 * QuotePriceSummary). Lets an operator booking on behalf of a client replace the calculated trip price
 * with a fixed amount for ANY transfer type. Confirming sets `fixedPrice`, which flows into the booking
 * payload as `fixed_price` and re-quotes (fees/levies recompute on top); resetting clears it back to the
 * originally calculated price. Both actions gate behind a confirmation modal (Figma 355×565 "Override
 * Price" / "Override Price / Saved").
 *
 * State machine: idle (value + pencil, plus a reset when an override is active) → editing (AUD$ input
 * with cancel/confirm) → Confirm modal → committed. The reset icon opens the Reset modal.
 */
export const JourneyCostOverride = ({ calculatedValue }: JourneyCostOverrideProps) => {
  const dispatch = useAppDispatch();
  const fixedPrice = useAppSelector(selectFixedPrice);
  const overrideActive = fixedPrice != null;

  // The amount shown when idle: the operator's override if set, else the calculated cost.
  const displayValue = overrideActive ? fixedPrice : calculatedValue;
  // A fixed-rate transfer type with no price yet has nothing to show — prompt entry instead of a value.
  const awaitingFirstPrice = displayValue == null;

  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input when entering edit mode.
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const parsed = Number(draftText);
  const entered = draftText.trim() !== "" && !Number.isNaN(parsed);
  const draftValid = entered && parsed >= MIN_OVERRIDE;
  // Show the minimum hint only once they've typed something below it (not on an empty field).
  const belowMinimum = entered && parsed < MIN_OVERRIDE;

  const startEditing = () => {
    // Seed the input with the current amount (blank while awaiting the first price).
    setDraftText(displayValue != null ? String(displayValue) : "");
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraftText("");
  };

  const commitOverride = () => {
    dispatch(setFixedPrice(Math.round(parsed * 100) / 100));
    setConfirmOpen(false);
    setEditing(false);
    setDraftText("");
  };

  const commitReset = () => {
    dispatch(setFixedPrice(undefined));
    setResetOpen(false);
  };

  const iconButton = (label: string, name: string, onClick: () => void, className?: string) => (
    <button type="button" aria-label={label} onClick={onClick} className={clsx("shrink-0 hover:opacity-80", className)}>
      <Icon name={name} size="sm" />
    </button>
  );

  return (
    <>
      {editing ? (
        <div className="flex flex-col items-end gap-1">
          {/* "AUD $" chip + amount via the AddOnTextInput atom; cancel/confirm circles 8px apart (Figma). */}
          <div className="flex items-center gap-2">
            <AddOnTextInput
              ref={inputRef}
              size="sm"
              className="w-32"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              maxLength={8}
              startAddOn="AUD $"
              placeholder="0.00"
              hasError={belowMinimum}
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && draftValid) setConfirmOpen(true);
                if (e.key === "Escape") cancelEditing();
              }}
            />
            <button
              type="button"
              aria-label="Cancel price override"
              onClick={cancelEditing}
              className="shrink-0 text-neutral-dark-gray hover:opacity-80"
            >
              <Icon name="CloseCircle" variant="Bold" size="xs" />
            </button>
            <button
              type="button"
              aria-label="Confirm price override"
              onClick={() => draftValid && setConfirmOpen(true)}
              className={clsx("shrink-0 hover:opacity-80", draftValid ? "text-success" : "pointer-events-none text-neutral-mid-gray")}
            >
              <Icon name="TickCircle" variant="Bold" size="xs" />
            </button>
          </div>
          {belowMinimum && (
            <Typography variant="small-regular" className="text-danger">
              {`Minimum ${fmt(MIN_OVERRIDE)}`}
            </Typography>
          )}
        </div>
      ) : awaitingFirstPrice ? (
        <Button variant="custom" size="sm" className="text-info hover:underline" onClick={startEditing}>
          Set price
        </Button>
      ) : (
        <div className="flex items-center gap-2">
          <Typography variant="action" className="whitespace-nowrap">
            {fmt(displayValue)}
          </Typography>
          {iconButton("Edit price override", "Edit2", startEditing, "text-neutral-dark-gray hover:text-primary")}
          {overrideActive && iconButton("Reset price override", "Refresh2", () => setResetOpen(true), "text-neutral-dark-gray hover:text-primary")}
        </div>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        variant="web-small"
        className="!max-w-[400px]"
        title="Confirm Price Override"
        description="This will replace the calculated trip price and booking fee with your set fixed amount. The client will receive and be charged this updated price."
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="md" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={commitOverride}>
              Confirm
            </Button>
          </>
        )}
      />

      <Modal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        variant="web-small"
        className="!max-w-[400px]"
        title="Reset Price Override"
        description="This will remove the overridden trip price and revert back to the originally calculated price for this booking."
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="md" onClick={() => setResetOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={commitReset}>
              Confirm
            </Button>
          </>
        )}
      />
    </>
  );
};
